import React from "react";
import { useTranslation } from "react-i18next";
import { FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ExportTargetType } from "@/types/data-export";

interface ExportActionCardProps {
  targetType: ExportTargetType;
  description: string;
  buttonLabel: string;
  onExport: () => void;
  /** A non-terminal export already exists for this target. */
  inFlight?: boolean;
  onViewInFlight?: () => void;
}

export const ExportActionCard: React.FC<ExportActionCardProps> = ({
  targetType,
  description,
  buttonLabel,
  onExport,
  inFlight = false,
  onViewInFlight,
}) => {
  const { t } = useTranslation();
  const targetWord = t(`dataExport.targetWord.${targetType}`);

  return (
    <div className="flex flex-col gap-4 rounded-2xl border bg-background p-6">
      <div className="flex items-center gap-2 text-foreground">
        <FileDown className="size-5 text-royal-glow-3" />
        <h2 className="text-lg font-semibold">
          {t("dataExport.action.title")}
        </h2>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>

      {inFlight ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm font-medium text-foreground">
            {t("dataExport.action.inFlight", { target: targetWord })}
          </p>
          {onViewInFlight ? (
            <Button
              type="button"
              variant="new_outline"
              onClick={onViewInFlight}
            >
              {t("dataExport.action.viewInFlight")}
            </Button>
          ) : null}
        </div>
      ) : (
        <div>
          <Button type="button" variant="new_outline" onClick={onExport}>
            <FileDown className="size-4" />
            {buttonLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
